import React from "react";
import styled from "styled-components";
import { ModalProps } from "../types";
import IntlStore from "stories/IntlStore";
import { FormattedMessage } from "react-intl";
import { useRelease } from "components/NewRelease/hooks/useRelease";

const PlatFormModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  step,
  setStep,
  releaseType    
}) => {
  if (!isOpen) return null;

  const { updateRelease } = useRelease();

  const data = [
    {
      id: 1,
      text: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data1Text",
        defaultMessage: "Switch to Album if you want to add more tracks."
      }),
      description: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data1Description",
        defaultMessage: "Are you sure you want to switch to Album?"
      }),
      link: "Album",
      type: "ALBUM"
    },
    {
      id: 2,
      text: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data2Text",
        defaultMessage: "When switching to Single, some added tracks will be removed."
      }),
      description: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data2Description",
        defaultMessage: "Are you sure you want to switch to Single?"
      }),
      link: "Single",
      type: "SINGLE"
    },
    {
      id: 3,
      text: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data3Text",
        defaultMessage: "The Music Video format cannot be combined with EP. When switching to Music Video,"
      }),
      description: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data3Description",
        defaultMessage:
          "your uploaded tracks will be deleted. Are you sure you want to switch to Music Video?"
      }),
      link: "Music Video",
      link1: "EP",
      type: "CLIP"
    },
    {
      id: 4,
      text: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data4Text",
        defaultMessage:
          "The TikTok Sound format cannot be combined with EP. When switching to TikTok Sound,"
      }),
      description: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data4Description",
        defaultMessage:
          "Your uploaded tracks will be deleted. Are you sure you want to switch to TikTok Sound?"
      }),
      link: "TikTok Sound",
      link1: "EP",
      type: "TIKTOK_SOUND"
    },
    {
      id: 5,
      text: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data5Text",
        defaultMessage: "Ringtone format cannot be combined with EP."
      }),
      description: IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.Data5Description",
        defaultMessage:
          "Your uploaded tracks will be deleted. Are you sure you want to switch to Ringtone?"
      }),
      link: "Ringtone",
      link1: "EP",
      type: "RINGTONE"
    }
  ];

  const currentData = data.find(item => item.type === releaseType);  
  if (!currentData) return null;

  const { text, description, link = "", link1 = "" , id} = currentData;  

  const renderWithLinks = (
    fullText: string,
    primaryLink: string,
    secondaryLink?: string
  ) => {
    const links = [primaryLink, secondaryLink].filter(Boolean) as string[];
    if (links.length === 0) return <>{fullText}</>;

    const regex = new RegExp(
      `(${links.map(l => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
      "g"
    );
    const parts = fullText.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          links.includes(part) ? (
            <a key={index} href="#">
              {part}
            </a>
          ) : (
            <React.Fragment key={index}>{part}</React.Fragment>
          )
        )}
      </>
    );
  };

  return (
    <Overlay>
      <ModalContainer>
        <ModalText>
          <Text>{renderWithLinks(text, currentData.link, currentData.link1)}</Text>
          <Text>
            {renderWithLinks(description, currentData.link, currentData.link1)}
          </Text>
        </ModalText>

        <ButtonsWrapper>
          <Button variant="primary" onClick={async()=>{onConfirm(),  await updateRelease({
          id: id,
          release_type_id: id,
        });}}>
            Yes
          </Button>
          <Button variant="default" onClick={onClose}>
            <FormattedMessage id="NewRelease.ReleaseInfo.No" defaultMessage="No" />
          </Button>
        </ButtonsWrapper>
      </ModalContainer>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const ModalContainer = styled.div`
  background: white;
  width: 793px;
  height: 242px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ModalText = styled.p`
  display: flex;
  flex-direction: column;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;
  text-align: center;
  color: #000;
  margin: 0;

  a {
    color: #6c63ff;
    text-decoration: none;
    font-weight: 500;
  }
`;

const Text = styled.div``;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 7px;
  padding-top: 17px;
`;

const Button = styled.button<{ variant?: "primary" | "default" }>`
  width: 92px;
  height: 46px;
  border-width: 1px;
  border-radius: 10px;
  border: ${({ variant }) =>
    variant === "default" ? "1px solid #000" : "none"};
  background-color: ${({ variant }) =>
    variant === "primary" ? "#6c63ff" : "#fff"};
  color: ${({ variant }) => (variant === "primary" ? "#fff" : "#000")};
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

export default PlatFormModal;
